import { PedigreeListDto } from '@common/dto/pedigree/list-dto';
import * as React from 'react';
import { getInvites } from '../services/invites';
import { getInvitesColumns, saveInvitesColumns } from '@common/actions/invites';
import { ColumnListDto } from '@common/dto/column/list-dto';
import { NewTableLoadMetadata, NewTableSortingDirection } from '@common/components/new-table';
import { InviteListDto } from '@common/dto/invite/list-dto';
import { generateId } from '@common/utils/common';
import { restoreInvitesColumnsToDefault as restoreInvitesColumnsToDefaultBase, UpdateInviteStatusParams, updateInviteStatus, updateReviewedStatusActions } from '@common/actions/invites';
import { InviteReviewedStatusStateUpdateDto } from "@common/dto/invite-reviewed-status-state/update-dto";
import { InviteStatus } from "@common/dto/invite-status";
import { HttpErrorDto } from '@common/dto/http';
import { I18n } from 'react-redux-i18n';
import { InviteUpdateLockStatusParams, updateLockStatus } from '@common/actions/individuals';

export type InvitesControllerProps = {
  showErrorMessage: (msg: string) => void;
  pedigreesSelectedItems: Array<PedigreeListDto>;
  getColumnId: (col: ColumnListDto) => string;
};

export const useInvitesController = (props: InvitesControllerProps) => {
  const { getColumnId, showErrorMessage, pedigreesSelectedItems } = props;
  const inviteItemUniqProperty = 'customInviteItemUniqProperty';

  const [invitesLoadMetadata, setInvitesLoadMetadata] = React.useState<NewTableLoadMetadata>({
    pagination: {
      size: 50,
      start: 0,
    },
  });
  const [invitesCount, setInvitesCount] = React.useState(0);
  const [invites, setInvites] = React.useState<Array<InviteListDto>>([]);
  const [invitesLoading, setInvitesLoading] = React.useState(false);
  const [invitesColumns, setInvitesColumns] = React.useState<Array<ColumnListDto>>([]);
  const [invitesSelectedItems, setInvitesSelectedItems] = React.useState<Array<InviteListDto>>([]);

  const handleInviteSelectedItemsChange = React.useCallback((individuals: InviteListDto[]) => {
    setInvitesSelectedItems(individuals);
  }, []);

  const handleGetInvites = async (params: NewTableLoadMetadata & { columns: Array<ColumnListDto>; pedigreeId: PedigreeListDto['id']; loadedInvites: Array<InviteListDto> }) => {
    try {
      setInvitesLoading(true);

      const { filtering, pagination, sorting } = params;

      const columns = params.columns.map((x): ColumnListDto => {
        const isSorted = getColumnId(x) === sorting?.id;
  
        return {
          ...x,
          sortOrder: isSorted ? 1 : 0,
          sortAsc: isSorted ? sorting?.direction === NewTableSortingDirection.asc : false,
        };
      });
  
      const filters = filtering?.columns || {};
      const columnFiltersProcessed = Object.keys(filters).map((columnId) => {
        const columnIndex = params.columns.findIndex((x) => getColumnId(x) == columnId);
        const columnToRemoveBecauseOfActionsColumn = 1;
        const value = filters[columnId];
  
        return {
          columnId: columnIndex + columnToRemoveBecauseOfActionsColumn,
          operator1: value?.operator1 ?? null,
          operator2: value?.operator2 ?? null,
          value1: value?.whereClause1 ?? null,
          value2: value?.whereClause2 ?? null,
          andOr: value?.andOR,
        };
      });

      const { data, totalCount } = await getInvites({
        pagination,
        filtering: {
          filterApplied: filtering?.filter ?? false,
          columnFilters: columnFiltersProcessed,
          columns,
          pedigreeId: params.pedigreeId,
        },
      });

      const processedData = data.map((x) => {
        (x as Record<string, any>)[inviteItemUniqProperty] = generateId();

        return x;
      });

      const finalData = pagination.start > 0
        ? [...params.loadedInvites, ...processedData]
        : processedData;

      setInvites(finalData);
      setInvitesCount((x) => pagination.start > 0 ? x : totalCount);
      setInvitesLoadMetadata({
        pagination,
        sorting,
        filtering
      });

    } finally {
      setInvitesLoading(false);
    }
  };

  const updateInvitesColumns = React.useCallback(async (columns: Array<ColumnListDto>) => {
    await saveInvitesColumns(columns);
    setInvitesColumns(columns);
  }, []);

  const getAndSetInvitesColumns = React.useCallback(async () => {
    const cols = await getInvitesColumns();
    setInvitesColumns(cols);
  }, []);

  const resetInvitesList = React.useCallback(() => {
    setInvitesLoadMetadata((x) => ({
      pagination: {
        start: 0,
        size: x.pagination.size,
      },
      sorting: undefined,
      filtering: undefined,
    }));
    setInvitesCount(0);
    setInvites([]);
    setInvitesSelectedItems([]);
  }, []);

  const restoreInvitesColumnsToDefault = React.useCallback(async () => {
    const cols = await restoreInvitesColumnsToDefaultBase();
    setInvitesColumns(cols);
    return cols;
  }, []);

  const onRefreshInvitesOrChangeInvitesColumns = () => {
    setInvitesSelectedItems([]);
  };

  const handleUpdateInviteStatus = async (data: UpdateInviteStatusParams) => {
    await updateInviteStatus(data);      

    if (pedigreesSelectedItems.length) {
      await handleGetInvites({
        columns: invitesColumns,
        pedigreeId: pedigreesSelectedItems[0]?.id!,
        loadedInvites: [],
        sorting: invitesLoadMetadata.sorting,
        filtering: invitesLoadMetadata.filtering,
        pagination: {
          start: 0,
          size: 45,
        },
      }).then(() => {
        onRefreshInvitesOrChangeInvitesColumns();
      }).catch((err) => {
        console.error(err);
        const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get invites');
        showErrorMessage(errorMessage);
      });
    } else {
      onRefreshInvitesOrChangeInvitesColumns();
    }
  };

  const handleUpdateReviewedStatusActions = async ({ inviteIds, ...restData }: InviteReviewedStatusStateUpdateDto) => {
    const processedInviteIds = inviteIds.map((x) => ({ ...x, fhq4: x.fhq4 }));
    await handleUpdateInviteStatus({
      inviteIds: processedInviteIds,
      status: InviteStatus.reviewed,
    });
    
    await updateReviewedStatusActions({
      inviteIds: processedInviteIds,
      ...restData,
    });

    if (pedigreesSelectedItems.length) {
      handleGetInvites({
        columns: invitesColumns,
        pedigreeId: pedigreesSelectedItems[0]?.id!,
        loadedInvites: [],
        sorting: invitesLoadMetadata.sorting,
        filtering: invitesLoadMetadata.filtering,
        pagination: {
          start: 0,
          size: 45,
        },
      }).then(() => {
        onRefreshInvitesOrChangeInvitesColumns();
      }).catch((err) => {
        console.error(err);
        const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get invites');
        showErrorMessage(errorMessage);
      });
    } else {
      onRefreshInvitesOrChangeInvitesColumns();
    }
  };

  const handleInvitesColumnsOrderChange = async (columns: Array<ColumnListDto>) => {
    const currentSortingColumnExists = columns.some((x) => getColumnId(x) === invitesLoadMetadata?.sorting?.id);
    const newInvitesLoadMetadata: NewTableLoadMetadata = {
      sorting: currentSortingColumnExists 
        ? invitesLoadMetadata?.sorting!
        : undefined,
      pagination: {
        start: 0,
        size: 45,
      },
    };

    await updateInvitesColumns(columns);
    await getAndSetInvitesColumns();

    if (pedigreesSelectedItems.length) {
      handleGetInvites({
        columns,
        pedigreeId: pedigreesSelectedItems[0]?.id!,
        loadedInvites: [],
        ...newInvitesLoadMetadata
      }).then(() => {
        onRefreshInvitesOrChangeInvitesColumns();
      }).catch((err) => {
        console.error(err);
        const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get invites');
        showErrorMessage(errorMessage);
      });
    } else {
      onRefreshInvitesOrChangeInvitesColumns();
    }
  };

  const handleUpdateInvitesLockStatus = async (data: Array<InviteUpdateLockStatusParams>) => {
    await Promise.all(data.map(updateLockStatus));

    if (pedigreesSelectedItems.length) {
      await handleGetInvites({
        columns: invitesColumns,
        pedigreeId: pedigreesSelectedItems[0]?.id!,
        loadedInvites: [],
        sorting: invitesLoadMetadata.sorting,
        filtering: invitesLoadMetadata.filtering,
        pagination: {
          start: 0,
          size: 45,
        },
      }).then(() => {
        onRefreshInvitesOrChangeInvitesColumns();
      }).catch((err) => {
        console.error(err);
        const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get invites');
        showErrorMessage(errorMessage);
      });
    } else {
      onRefreshInvitesOrChangeInvitesColumns();
    }
  };

  const handleRestoreInvitesColumnsToDefault = async () => {
    const columns = await restoreInvitesColumnsToDefault();

    if (pedigreesSelectedItems.length) {
      handleGetInvites({
        columns,
        pedigreeId: pedigreesSelectedItems[0]?.id!,
        loadedInvites: [],
        sorting: undefined,
        filtering: undefined,
        pagination: {
          start: 0,
          size: 45,
        },
      }).then(() => {
        onRefreshInvitesOrChangeInvitesColumns();
      }).catch((err) => {
        console.error(err);
        const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get invites');
        showErrorMessage(errorMessage);
      });
    } else {
      onRefreshInvitesOrChangeInvitesColumns();
    }
  };

  return {
    handleInviteSelectedItemsChange,
    handleRestoreInvitesColumnsToDefault,
    handleUpdateInvitesLockStatus,
    handleInvitesColumnsOrderChange,
    onRefreshInvitesOrChangeInvitesColumns,
    handleUpdateInviteStatus,
    handleUpdateReviewedStatusActions,
    inviteItemUniqProperty,
    resetInvitesList,
    invitesLoadMetadata,
    invites,
    invitesLoading,
    invitesCount,
    handleGetInvites,
    invitesSelectedItems,
    invitesColumns,
    getAndSetInvitesColumns,
    updateInvitesColumns,
  };
};